import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  Modal,
  Button,
  DatePicker,
  CheckBox,
} from "@etframework/ui.components";
import { useUpdateExtendedDate } from "../../data/mutations/taskMutations";
import { Trans, useTranslation } from "react-i18next";
import useModalEvent from "../../hooks/useModalEvent";
import { toast } from "react-toastify";
import { ValidationProvider, useValidation } from "@etframework/ui.components";
import { formatDate } from "../../utils/helpers";
import useEvent from "../../hooks/useEvent";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { RuleDropdown } from "../../components/OverlayComponents/RuleDropdown";
import { useDueDatesAuth } from "../../hooks/useDueDatesAuth";

export const ExtendedDateModal = () => {
  return (
    <ValidationProvider>
      <InternalExtendedDateModal />
    </ValidationProvider>
  );
};
export const InternalExtendedDateModal = () => {
  const { setValidationErrors, clearValidationErrors } = useValidation();
  const extendedDateRef = useRef(null);
  const applyExtensionRef = useRef(null);
  const ruleDropdownRef = useRef(null);

  const [extensionDate, setExtensionDate] = useState(null);
  const [extendedDate, setExtendedDate] = useState(null);
  const [task, setTask] = useState(false);
  const [rule, setRule] = useState(null);

  const date = task?.TaskTaxReturnInfo?.EffectiveTaxDeadline;

  const { UserId } = useDueDatesAuth();
  const queryClient = useQueryClient();
  const { Events, toggleEvent } = useEvent();
  const { t, i18n } = useTranslation(["Overlay"], {
    useSuspense: false,
  });

  const { mutate: updateExtendedDate, isPending } = useUpdateExtendedDate();

  //Modal
  const modalRef = useRef();
  const { ModalEvents, useRegisterEvent } = useModalEvent();
  const [showModal, setShowModal] = useState(false);

  const closeModal = () => {
    setExtendedDate(null);
    setExtensionDate(null);
    setRule(null);
    clearValidationErrors();
    setShowModal(false);
    toggleEvent(Events.ClosingExtendedDateModal);
  };

  const openModal = () => {
    setShowModal(true);
    modalRef?.current?.open();
  };

  const toggleModal = (event) => {
    setTask(event.detail.task);
    setRule(
      event.detail.task?.Template?.DueDateInfo?.HasExtensionMonth ? 1 : 2
    );

    if (showModal) {
      closeModal();
      return;
    } else {
      openModal();
    }
  };

  const ModalContent = () => {
    const checkRequiredData = () => {
      if (rule !== 2) return true;
      const dateValue = extendedDateRef.current.getValue();
      const isDateValid = dateValue != null;
      const isValid = isDateValid;

      if (!isDateValid) extendedDateRef.current.setError(t("Required"));

      extendedDateRef.current.setShowError(!isDateValid);

      return isValid;
    };

    const onUpdateExtendedDate = () => {
      if (checkRequiredData()) {
        const extendedDateValue = extendedDateRef.current?.getValue();

        extendedDateValue?.setHours(23);
        extendedDateValue?.setMinutes(59);
        extendedDateValue?.setSeconds(59);
        extendedDateValue?.setMilliseconds(0);

        const postData = {
          Id: task?.Id,
          ExtendedDate: extendedDateValue,
          Date: date,
          ApplyTemplateExtension: rule == 1,
          ApplyToDueDate: applyExtensionRef.current?.checked,
          CurrentUserId: UserId,
        };

        updateExtendedDate(
          { task: postData },
          {
            onSuccess: () => {
              closeModal();
              queryClient.invalidateQueries(["get-task", task?.Id]);
              toggleEvent(Events.RefreshTaskGrid);
              toast.success(t("Success"), {
                position: "bottom-left",
              });
            },
            onError: (errors) => {
              setValidationErrors(errors?.data);
              toast.error(t("ErrorMsg"), { position: "bottom-left" });
            },
          }
        );
      }
    };

    useEffect(() => {
      if (task?.Template?.DueDateInfo?.ExtensionMonth) {
        const dateToUtc = moment.utc(date);
        const newDate = new Date(dateToUtc);
        setExtensionDate(
          newDate.setMonth(
            newDate.getMonth() + task?.Template?.DueDateInfo?.ExtensionMonth
          )
        );
      }
    }, []);

    return (
      <div className="et-extended-duedate-modal">
        <RuleDropdown
          ref={ruleDropdownRef}
          onChange={setRule}
          componentRule={rule}
          hasExtensionRule={task?.Template?.DueDateInfo?.HasExtensionMonth}
        />

        <div>
          {rule == 2 ? (
            <div className="col-6">
              <DatePicker
                ref={extendedDateRef}
                value={extendedDate}
                name="ExtendedDate"
                id={ModalEvents.ExtendedDateModal.Id + "_date-picker"}
                language={i18n.language}
                change={(e) => {
                  setExtendedDate(e.value);
                  clearValidationErrors();
                }}
              />
            </div>
          ) : (
            <div className="bg-lightest-gray br-4">
              <div className="px-3 py-4">
                <div>
                  <Trans components={{ b: <strong /> }}>
                    {t("ExtendDateModal.Rule.RuleFor1", {
                      template: task?.Template?.Name,
                      months: task?.Template?.DueDateInfo?.ExtensionMonth,
                    })}
                  </Trans>
                </div>
                <div className="mt-3">
                  {t("ExtendDateModal.Rule.RuleFor2", {
                    date: formatDate(extensionDate, "MMMM D, YYYY"),
                  })}
                </div>
              </div>
            </div>
          )}

          <div className="my-4">
            <CheckBox
              ref={applyExtensionRef}
              name="ApplyExtensionToDueDate"
              id={ModalEvents.ExtendedDateModal.Id + "_apply-extension"}
              label={t(
                `ExtendDateModal.${
                  rule == 1 ? "Rule" : "Date"
                }.ApplyExtensionToDueDate`
              )}
            />
          </div>
        </div>

        <div className="d-flex justify-content-end gap-3 mt-5">
          <Button
            text={t("Cancel")}
            className="btn-tertiary w-auto"
            onClick={closeModal}
          />
          <Button
            id={ModalEvents.ExtendedDateModal.Id + "_apply-button"}
            text={t("ExtendDateModal.Confirm")}
            className="btn btn-primary w-auto"
            onClick={onUpdateExtendedDate}
            loading={isPending}
          />
        </div>
      </div>
    );
  };

  useRegisterEvent(ModalEvents.ExtendedDateModal, toggleModal);

  return ReactDOM.createPortal(
    <>
      <Modal
        id={ModalEvents.ExtendedDateModal.Id}
        ref={modalRef}
        isModal
        showCloseIcon={true}
        visible={showModal}
        content={<ModalContent />}
        close={closeModal}
        minHeight="20vh"
        width="500px"
      ></Modal>
    </>,
    document.body
  );
};
